import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { LuBot } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { AiCredential } from '../../entities/ai-credential';
import { CODE_AGENT_PROVIDER_LABEL, CodeAgentProvider } from '../../entities/code-agent-task';
import {
  aiCredentialErrorMessage,
  getDefaultAiProvider,
  listAiCredentials,
  setDefaultAiProvider,
} from '../../shared/api/ai-credentials';

const HOSTED = 'hosted' as const;
type ProviderChoice = typeof HOSTED | CodeAgentProvider;

// MF-42/MF-43 — lets a user route the system agents' LLM calls (today: the Standalone chat
// agent) through their own saved BYOK key instead of mFlow's hosted key, as a persisted default.
// Only offers providers with a saved credential (widgets/integrations/AiProviderIntegration.tsx
// is where those are added) — the same restriction the BE enforces server-side.
const AiAgentPreferences: React.FC = () => {
  const [credentials, setCredentials] = useState<AiCredential[]>([]);
  const [defaultProvider, setDefaultProviderState] = useState<CodeAgentProvider | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listAiCredentials(), getDefaultAiProvider()])
      .then(([creds, provider]) => {
        if (cancelled) return;
        setCredentials(creds);
        setDefaultProviderState(provider);
      })
      .catch(() => !cancelled && setLoadError('Failed to load AI agent preferences.'))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const choice: ProviderChoice = defaultProvider ?? HOSTED;

  const handleChange = async (next: ProviderChoice) => {
    const nextProvider = next === HOSTED ? null : next;
    const previous = defaultProvider;
    setDefaultProviderState(nextProvider);
    setIsSaving(true);
    setError(null);
    try {
      await setDefaultAiProvider(nextProvider);
      toast.success(
        nextProvider
          ? `System agents will now use your ${CODE_AGENT_PROVIDER_LABEL[nextProvider]} key.`
          : 'System agents will use mFlow’s hosted key again.',
      );
    } catch (err) {
      setDefaultProviderState(previous);
      setError(aiCredentialErrorMessage(err) ?? 'Failed to update the default provider.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="card bg-base-100 shadow-card">
        <div className="card-body p-4 space-y-3">
          <div className="skeleton h-5 w-48" />
          <div className="skeleton h-12 w-full" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="alert alert-error shadow-card">
        <span>{loadError}</span>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-card">
      <div className="card-body p-4 gap-3">
        <div className="flex items-start gap-3">
          <span className="text-lg shrink-0 opacity-60">
            <LuBot />
          </span>
          <div>
            <div className="font-semibold">Bring your own key</div>
            <div className="text-sm opacity-70 max-w-lg">
              Choose which key mFlow&apos;s system agents (like the Standalone chat assistant) use for their LLM calls.
              This does not affect the coding agent, which has its own provider choice per task.
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-error py-2">
            <span className="text-sm">{error}</span>
          </div>
        )}

        {credentials.length === 0 ? (
          <p className="text-sm opacity-50">
            Add an API key in{' '}
            <Link to="/integrations" className="link link-primary">
              Integrations
            </Link>{' '}
            to use it here.
          </p>
        ) : (
          <label className="form-control w-full max-w-xs">
            <span className="label-text text-xs mb-1 opacity-60">Default provider</span>
            <select
              className="select select-bordered select-sm w-full"
              value={choice}
              disabled={isSaving}
              onChange={(e) => handleChange(e.target.value as ProviderChoice)}
            >
              <option value={HOSTED}>mFlow-hosted (default)</option>
              {credentials.map((c) => (
                <option key={c.provider} value={c.provider}>
                  {CODE_AGENT_PROVIDER_LABEL[c.provider]} (•••• {c.keyHint})
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
    </div>
  );
};

export default AiAgentPreferences;
